import React, { useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import axios from "axios";
import BridgePage from "./BridgePage";
import ExecutivePage from "./ExecutivePage";
// EIOS access requires founder Google sign-in (Emergent-managed auth). Backend
// enforcement lives in routes/auth.py; this routing is presentation only.
import AppShell from "./AppShell";
import { LoginPage, AuthCallback, ProtectedRoute } from "./auth/AuthGate";
import PlatformsPage from "./site/PlatformsPage";
import PlatformDetailPage from "./site/PlatformDetailPage";
import AboutPage from "./site/AboutPage";
import TrustPage from "./site/TrustPage";
import ContactPage from "./site/ContactPage";
import LegalPage from "./site/LegalPage";
import { SiteFooter, MobileNavMenu } from "./site/SiteChrome";
import {
  Sparkles,
  Stethoscope,
  HeartPulse,
  Wallet,
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  FileBadge,
  Lock,
  Cpu,
  GraduationCap,
  Brain,
  MessageSquare,
  CalendarCheck,
  Receipt,
  ClipboardPlus,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ============================================================
   Particle background canvas
   ============================================================ */
const ParticleField = () => {
  const ref = useRef(null);

  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let raf;
    let particles = [];
    const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;

    const resize = () => {
      c.width = window.innerWidth;
      c.height = window.innerHeight;
      const count = reduced ? 20 : isMobile ? 40 : 90;
      particles = Array.from({ length: count }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        r: Math.random() * 1.6 + 0.3,
        vx: (Math.random() - 0.5) * 0.18,
        vy: (Math.random() - 0.5) * 0.18,
        hue: Math.random() > 0.5 ? "0,229,204" : "0,102,255",
        a: Math.random() * 0.5 + 0.2,
      }));
    };
    resize();
    window.addEventListener("resize", resize);

    const draw = () => {
      ctx.clearRect(0, 0, c.width, c.height);
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > c.width) p.vx *= -1;
        if (p.y < 0 || p.y > c.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.hue},${p.a})`;
        ctx.fill();
      });

      // Subtle connections
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 120) {
            ctx.strokeStyle = `rgba(0,229,204,${0.06 * (1 - d / 120)})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      raf = requestAnimationFrame(draw);
    };
    if (!reduced) draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return <canvas ref={ref} className="particle-canvas" aria-hidden="true" />;
};

/* ============================================================
   Neural network canvas (ARIA section)
   ============================================================ */
const NeuralNet = () => {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    let raf;
    let nodes = [];
    const setup = () => {
      const r = c.getBoundingClientRect();
      c.width = r.width;
      c.height = r.height;
      const count = window.innerWidth < 768 ? 18 : 38;
      nodes = Array.from({ length: count }, () => ({
        x: Math.random() * c.width,
        y: Math.random() * c.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        pulse: Math.random() * Math.PI * 2,
      }));
    };
    setup();
    window.addEventListener("resize", setup);

    const draw = (t) => {
      ctx.clearRect(0, 0, c.width, c.height);
      nodes.forEach((n) => {
        n.x += n.vx;
        n.y += n.vy;
        if (n.x < 0 || n.x > c.width) n.vx *= -1;
        if (n.y < 0 || n.y > c.height) n.vy *= -1;
        const pulse = (Math.sin(t / 600 + n.pulse) + 1) / 2;
        ctx.beginPath();
        ctx.arc(n.x, n.y, 1.5 + pulse * 1.5, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,229,204,${0.4 + pulse * 0.4})`;
        ctx.fill();
      });
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const d = Math.hypot(dx, dy);
          if (d < 180) {
            ctx.strokeStyle = `rgba(0,102,255,${0.18 * (1 - d / 180)})`;
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", setup);
    };
  }, []);
  return <canvas ref={ref} className="neural-canvas" aria-hidden="true" />;
};

/* ============================================================
   Scroll reveal hook
   ============================================================ */
const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

/* ============================================================
   Nav
   ============================================================ */
const Nav = ({ onCta }) => {
  return (
    <header className="nav-wrap" data-testid="main-navigation">
      <div className="nav-inner">
        <a href="#top" className="nav-brand" data-testid="nav-logo">
          <span className="brand-mark">
            <img src="/brand/camelion-emblem.png" alt="" />
          </span>
          <span className="brand-text">
            Camelion<span className="accent">AI</span>
          </span>
        </a>
        <ul className="nav-links">
          <li><a href="#platforms" data-testid="nav-link-platforms">Platforms</a></li>
          <li><a href="/executive" data-testid="nav-link-executive">Executive</a></li>
          <li><a href="/bridge" data-testid="nav-link-bridge">Bridge</a></li>
          <li><a href="#aria" data-testid="nav-link-aria">ARIA</a></li>
          <li><a href="#journey" data-testid="nav-link-journey">Journey</a></li>
          <li><a href="#founder" data-testid="nav-link-founder">Founder</a></li>
          <li><a href="/about" data-testid="nav-link-about">About</a></li>
          <li><a href="/trust" data-testid="nav-link-trust">Trust &amp; Tech</a></li>
          <li><a href="/contact" data-testid="nav-link-contact">Contact</a></li>
        </ul>
        <div className="nav-right">
          <button className="nav-cta" onClick={onCta} data-testid="nav-cta-btn">
            Get Beta Access
          </button>
          <MobileNavMenu />
        </div>
      </div>
    </header>
  );
};

/* ============================================================
   Hero
   ============================================================ */
const Hero = ({ onCta }) => {
  return (
    <section className="hero" id="top">
      <div className="hero-glow" />
      <div className="hero-inner">
        <div className="cham-wrap reveal in" data-testid="hero-chameleon">
          <div className="cham-ring r1" />
          <div className="cham-ring r2" />
          <div className="cham-ring r3" />
          <div className="cham-glow" />
          <img
            src="/brand/camelion-logo-full.png"
            alt="CamelionAI — chameleon compass emblem"
            className="cham-img"
          />
        </div>

        <div className="hero-eyebrow reveal in">
          <span className="eyebrow-dot" />
          Adaptive Healthcare Intelligence
        </div>

        <h1 className="hero-title reveal in" data-testid="hero-title">
          The future is <span className="grad-text">intelligent healthcare.</span>
        </h1>
        <p className="hero-sub reveal in" data-testid="hero-subtitle">
          CamelionAI adapts as healthcare evolves.
        </p>

        <div className="hero-ctas reveal in">
          <button className="btn-primary" onClick={onCta} data-testid="hero-demo-btn">
            Request a Demo <ArrowRight size={16} />
          </button>
          <button className="btn-glass" onClick={onCta} data-testid="hero-beta-btn">
            Get Beta Access <ArrowUpRight size={16} />
          </button>
        </div>

        <div className="trust-row reveal in" data-testid="trust-row">
          <span>Designed for HIPAA-aligned deployment</span>
          <span className="dot">·</span>
          <span>Patent Pending USPTO</span>
          <span className="dot">·</span>
          <span>Hiscox Insured</span>
          <span className="dot">·</span>
          <span>Powered by Claude</span>
          <span className="dot">·</span>
          <span>Built by a Prosthodontist</span>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   Platform card (with 3D tilt)
   ============================================================ */
const PlatformCard = ({ variant, icon: Icon, name, sub, tags, href, testId }) => {
  const cardRef = useRef(null);

  const onMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const r = card.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const rx = (0.5 - y) * 8;
    const ry = (x - 0.5) * 10;
    card.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-4px)`;
  };
  const onLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = "perspective(900px) rotateX(0) rotateY(0) translateY(0)";
  };

  return (
    <a
      ref={cardRef}
      className={`pcard p-${variant} reveal`}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={testId}
    >
      <div className="pcard-icon-wrap">
        <Icon size={24} />
      </div>
      <span className="pcard-beta">
        <span className="beta-dot" /> Beta Access Live
      </span>
      <div className="pcard-name">{name}</div>
      <p className="pcard-sub">{sub}</p>
      <div className="pcard-tags">
        {tags.map((t) => (
          <span key={t} className="pcard-tag">{t}</span>
        ))}
      </div>
      <span className="pcard-cta">
        Explore {name} <ArrowRight size={14} />
      </span>
    </a>
  );
};

/* ============================================================
   Platforms section
   ============================================================ */
const Platforms = () => (
  <section className="block" id="platforms" data-testid="platforms-section">
    <div className="reveal">
      <div className="section-label">
        <span className="label-line" /> The Ecosystem
      </div>
      <h2 className="section-title">
        Three platforms. <span className="grad-text">One adaptive intelligence.</span>
      </h2>
      <p className="section-lede">
        Purpose-built for clinical, medical, and revenue workflows — unified by ARIA, the adaptive AI engine powering every CamelionAI product.
      </p>
    </div>

    <div className="platforms">
      <PlatformCard
        variant="prostho"
        icon={Stethoscope}
        name="ProsthoAI"
        sub="Specialist-grade clinical decision support for dental professionals."
        tags={["Clinical AI", "Treatment Planning", "Case Acceptance", "Smile Showcase"]}
        href="https://prosthoai.com"
        testId="card-prostho"
      />
      <PlatformCard
        variant="zida"
        icon={HeartPulse}
        name="ZidaMD"
        sub="Advanced clinical reasoning support for physicians and medical practices."
        tags={["Clinical Reasoning", "ICD-10 2026", "Risk Calculators", "Physician AI"]}
        href="https://zidamd.com"
        testId="card-zida"
      />
      <PlatformCard
        variant="reimburse"
        icon={Wallet}
        name="ReimbursePro"
        sub="AI-powered medical and dental billing, prior auth, and claims appeals."
        tags={["Medical Billing", "Prior Auth", "Claims Appeals", "Patient Portal"]}
        href="https://reimburse.pro"
        testId="card-reimburse"
      />
    </div>
  </section>
);

/* ============================================================
   ARIA section
   ============================================================ */
const Aria = () => (
  <section className="block aria-block" id="aria" data-testid="aria-section" style={{ maxWidth: "none", margin: 0 }}>
    <NeuralNet />
    <div className="aria-content" style={{ maxWidth: 1320, margin: "0 auto", padding: "0 32px" }}>
      <div className="reveal">
        <div className="section-label">
          <span className="label-line" /> Meet ARIA
        </div>
        <h2 className="aria-headline">
          One intelligence. <span className="grad-text">Across every platform.</span>
        </h2>
        <p className="aria-body">
          ARIA — Adaptive Reasoning &amp; Intelligence Assistant — is the clinical AI engine powering all three CamelionAI platforms. ARIA reasons, adapts, and communicates across prosthodontic, medical, and billing workflows with the precision of a specialist and the speed of AI.
        </p>
        <div className="aria-pills">
          <span className="pill">Speaks British English</span>
          <span className="pill">Evidence-based reasoning</span>
          <span className="pill">Designed for HIPAA-aligned deployment</span>
        </div>
        <div className="waveform" aria-hidden="true">
          {Array.from({ length: 40 }).map((_, i) => (
            <span
              key={i}
              className="bar"
              style={{ animationDelay: `${(i % 12) * 0.08}s`, height: `${20 + Math.random() * 40}px` }}
            />
          ))}
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   Journey section
   ============================================================ */
const journeySteps = [
  { n: "01", t: "Inception", d: "ARIA generates the treatment plan.", I: ClipboardPlus },
  { n: "02", t: "Communication", d: "ARIA writes the patient letter.", I: MessageSquare },
  { n: "03", t: "Confirmation", d: "ARIA follows up until the patient schedules.", I: Brain },
  { n: "04", t: "Scheduling", d: "Case converts to appointment.", I: CalendarCheck },
  { n: "05", t: "Billing", d: "ReimbursePro claims the revenue.", I: Receipt },
];

const Journey = () => (
  <section className="block" id="journey" data-testid="journey-section">
    <div className="reveal">
      <div className="section-label">
        <span className="label-line" /> The Journey
      </div>
      <h2 className="section-title">
        From diagnosis to paid claim. <span className="grad-text">Without leaving the system.</span>
      </h2>
      <p className="section-lede">
        ARIA orchestrates the entire treatment lifecycle — clinical, communication, scheduling, and revenue — across the CamelionAI ecosystem.
      </p>
    </div>

    <div className="journey-flow reveal">
      <div className="j-line" />
      {journeySteps.map(({ n, t, d, I }) => (
        <div className="j-step" key={n}>
          <div className="j-icon-wrap">
            <I size={28} />
            <span className="j-num">{n}</span>
          </div>
          <div className="j-title">{t}</div>
          <div className="j-desc">{d}</div>
        </div>
      ))}
    </div>
  </section>
);

/* ============================================================
   Founder
   ============================================================ */
const Founder = () => (
  <section className="block" id="founder" data-testid="founder-section">
    <div className="founder-grid">
      <div className="reveal">
        <div className="section-label">
          <span className="label-line" /> The Founder
        </div>
        <h2 className="section-title">
          Built by a prosthodontist. <span className="grad-text">For every clinician.</span>
        </h2>
        <p className="founder-body">
          CamelionAI was founded by <strong>Dr. Ronik Seecharan, DMD</strong> — a practicing prosthodontist with 30 years of clinical experience and an Advanced Certificate in Prosthodontics from Tufts University School of Dental Medicine. Every module, every reasoning pathway, every clinical decision reflects real chairside experience.
        </p>
        <p className="founder-body">
          <strong>Built from real clinical experience and strengthened by thoughtful engineering.</strong>
        </p>
        <div className="credentials-row">
          <span>Patent Pending USPTO</span>
          <span className="dot">·</span>
          <span>30 Years Clinical</span>
          <span className="dot">·</span>
          <span>Tufts University</span>
          <span className="dot">·</span>
          <span>Advanced Certificate in Prosthodontics</span>
        </div>
      </div>

      <div className="founder-visual reveal">
        <div className="founder-portrait">
          <img
            src="/images/founder.png"
            alt="Dr. Ronik Seecharan, DMD — Founder & CEO, CamelionAI"
            data-testid="founder-portrait"
          />
        </div>
        <div className="quote-card" style={{ width: "100%" }}>
          <span className="quote-mark">&ldquo;</span>
          <p className="quote-text">This is the tool I wished I had.</p>
          <div className="quote-attr">
            <div><strong>Dr. Ronik Seecharan, DMD</strong></div>
            <div>Prosthodontist &amp; Founder</div>
            <div>Boca Raton, FL</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ============================================================
   Trust grid
   ============================================================ */
const trustItems = [
  { I: ShieldCheck, t: "Designed for HIPAA-aligned deployment", s: "Security architecture engineered for healthcare data from day one." },
  { I: FileBadge, t: "Patent Pending USPTO", s: "Three provisional patents on file." },
  { I: Lock, t: "Hiscox Tech E&O Insured", s: "Errors, omissions, and cyber coverage." },
  { I: Cpu, t: "Powered by Claude (Anthropic)", s: "Frontier-grade clinical reasoning." },
  { I: GraduationCap, t: "ACP Guidelines Aligned", s: "Built around the standards of care." },
  { I: Stethoscope, t: "Built by a Prosthodontist", s: "30 years of clinical reasoning, codified." },
];

const Trust = () => (
  <section className="block" id="trust" data-testid="trust-section">
    <div className="reveal">
      <div className="section-label">
        <span className="label-line" /> Trust &amp; Compliance
      </div>
      <h2 className="section-title">
        Healthcare-grade. <span className="grad-text">By design.</span>
      </h2>
      <p className="section-lede">
        CamelionAI is built to meet the operational realities of healthcare — security, evidence, and accountability at every layer.
      </p>
    </div>
    <div className="trust-grid">
      {trustItems.map(({ I, t, s }) => (
        <div className="trust-card reveal" key={t}>
          <span className="trust-card-icon"><I size={20} /></span>
          <div>
            <div className="trust-card-title">{t}</div>
            <div className="trust-card-sub">{s}</div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/* ============================================================
   CTA Banner
   ============================================================ */
const CTABanner = React.forwardRef((_, ref) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/waitlist`, { email, source: "homepage_cta" });
      setSuccess(true);
      setEmail("");
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Please enter a valid email.";
      setError(String(detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="block" ref={ref} data-testid="cta-section">
      <div className="cta-banner reveal">
        <div className="cta-banner-content">
          <div className="section-label" style={{ justifyContent: "center", color: "#fff" }}>
            <span className="label-line" style={{ background: "#fff" }} /> Join the beta
          </div>
          <h2 className="cta-headline">
            Join the beta. <span className="grad-text">Shape the future of clinical AI.</span>
          </h2>
          <p className="cta-sub">Request early access to ProsthoAI, ZidaMD, or ReimbursePro.</p>
          <form className="cta-form" onSubmit={submit} data-testid="waitlist-form">
            <input
              className="cta-input"
              type="email"
              required
              placeholder="you@practice.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="waitlist-email-input"
            />
            <button className="cta-submit" type="submit" disabled={loading} data-testid="waitlist-submit-btn">
              {loading ? "Requesting…" : "Request Access"}
            </button>
          </form>
          {success && (
            <div className="cta-success" data-testid="waitlist-success">
              ✓ You&apos;re on the list. We&apos;ll be in touch.
            </div>
          )}
          {error && (
            <div className="cta-success" style={{ color: "#ff7676" }} data-testid="waitlist-error">
              {error}
            </div>
          )}
          <div className="cta-note">Designed for HIPAA-aligned deployment · Synthetic data only during evaluation</div>
        </div>
      </div>
    </section>
  );
});
CTABanner.displayName = "CTABanner";

/* ============================================================
   Home
   ============================================================ */
const Home = () => {
  const ctaRef = useRef(null);
  useReveal();

  useEffect(() => {
    // Remove "Made with Emergent" badge from production site
    document.getElementById("emergent-badge")?.remove();
  }, []);

  const goToCta = () => {
    ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      document.querySelector('[data-testid="waitlist-email-input"]')?.focus();
    }, 700);
  };

  return (
    <>
      <ParticleField />
      <div className="grid-bg" />
      <Nav onCta={goToCta} />
      <Hero onCta={goToCta} />
      <Platforms />
      <Aria />
      <Journey />
      <Founder />
      <Trust />
      <CTABanner ref={ctaRef} />
      <SiteFooter />
    </>
  );
};

function RoutedApp() {
  const location = useLocation();
  // Session callback must be handled before any route renders (race-condition guard)
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/platforms" element={<PlatformsPage />} />
      <Route path="/platforms/:slug" element={<PlatformDetailPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/trust" element={<TrustPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy" element={<LegalPage kind="privacy" />} />
      <Route path="/terms" element={<LegalPage kind="terms" />} />
      <Route path="/bridge" element={<BridgePage />} />
      <Route path="/executive" element={<ExecutivePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/app/*" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <RoutedApp />
    </BrowserRouter>
  );
}

export default App;
